import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../utils/Loader";
import useCsr from "../hooks/useCsr";
import { usePermissions } from "../contexts/PermissionsContext";
import { NO_PERMISSION_TITLE } from "../utils/permissions";
import SidebarMain from "./SidebarMain";
import SidebarCSR from "./SidebarCSR";
import Footer from "./Footer";
import { CButton } from "@coreui/react";
import { ReactComponent as CloseMenu } from "../assets/images/icons/close.svg";
import { ReactComponent as ArrowRight } from "../assets/images/icons/arrow-right.svg";
import { ReactComponent as ArrowLeft } from "../assets/images/icons/arrow-left.svg";
import SidebarMainLogo from "../assets/images/logos/TriloDocs-Logo-Horizontal-POSITIVE.png";
import { useAuth0 } from "@auth0/auth0-react";

const DashboardContent = ({
  sidebarCSR,
  headerTitle,
  headerButton,
  pageContent,
  prevButton,
  nextButton,
  saveButton,
  saveButtonHandler,
  saveButtonDisabled = false,
  ignoreSaveErrors = false,
  nextButtonRightArrow = true,
  contentOverlay = false,
  savePermission = null,
  hideFooter = false,
}) => {
  const { user } = useAuth0();
  const { ajaxLoading, studyInfoLoading, headerStudyName } = useCsr();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  // On mobile the CSR steps sidebar is a drawer, opened from the header.
  const [stepsOpen, setStepsOpen] = useState(false);

  // Pages that write on save pass the permission that write needs (e.g.
  // csr:wizards:update), or several when the write needs all of them — the lay
  // summary PUT replaces the whole wizard, so it also needs the read that
  // populated the form. Without them Save is disabled and Next navigates on its
  // own instead of saving first.
  const requiredSavePermissions = savePermission
    ? [].concat(savePermission)
    : [];
  const canSave = requiredSavePermissions.every(hasPermission);
  const location = useLocation();
  // Picking a step navigates, so close the drawer behind it.
  useEffect(() => {
    setStepsOpen(false);
  }, [location.pathname]);
  // Every product captions its pages with the name of the record being worked
  // on. SCS keeps that name in its own slice; CSR, LS and PN have theirs
  // resolved by CsrProvider, which scopes it to the current product and id —
  // so the SCS name can't leak onto a CSR/LS/PN page, or vice versa.
  const scsSummaryName = useSelector(state => state.scsData.summaryName);
  const headerName = location.pathname.startsWith("/scs/")
    ? scsSummaryName
    : headerStudyName || "";
  // Custom navigation handler that first saves then navigates
  const handleNavigationWithSave = async targetPath => {
    if (saveButtonHandler && canSave) {
      try {
        // Show success toast and navigate after successful save

        const syntheticEvent = { preventDefault: () => {} };

        // Call the save handler and wait for it to complete
        const result = saveButtonHandler(syntheticEvent);
        let resolved;

        if (result && typeof result.then === "function") {
          resolved = await result;
        }

        // Only navigate if the save handler completed successfully
        // (if it returned undefined, validation failed; if resolved?.success === false, save failed silently)
        if (result !== undefined && !(resolved && resolved.success === false)) {
          navigate(targetPath);
          // Create a synthetic event for the save handler
          setTimeout(() => {
            toast.success("Data saved successfully!");
          }, 100);
        }
      } catch (error) {
        // If ignoreSaveErrors, stay on page with no error display
        if (ignoreSaveErrors) return;
        navigate(targetPath);
        console.warn(
          "Save failed during navigation, but proceeding with navigation:",
          error
        );
        setTimeout(() => {
          toast.error("Save failed, click on Save button to save changes.");
        }, 100);
      }
    } else {
      // If no save handler, just navigate
      navigate(targetPath);
    }
  };

  return (
    <div>
      <div className={"top"}>
        <div className={"logo"}>
          <img src={SidebarMainLogo} width="280px" alt="TriloDocs Logo" />
        </div>
        <div>
          <h6 className="welcome-title">Welcome {user?.name || "User"}</h6>
        </div>
      </div>
      <div>
        <div
          className={`dashboard container-wrapper ${sidebarCSR ? "container-wrapper__three" : ""} ${stepsOpen ? "container-wrapper--steps-open" : ""}`}>
          <SidebarMain />
          {sidebarCSR && <SidebarCSR />}
          {sidebarCSR && stepsOpen && (
            <div
              className={"sidebar__csr-backdrop"}
              data-testid="steps-backdrop"
              onClick={() => setStepsOpen(false)}
            />
          )}
          <div className={"container-wrapper__innner-container"}>
            <div className={"container-wrapper__inner"}>
              {headerTitle && (
                <div className={"dashboard-header dashboard-header__wrapper"}>
                  <div className={"dashboard-header__left"}>
                    {/* <div className={"eyebrow"}>{headerEyebrow}</div> */}
                    <div className="headerTitleText">{headerTitle}</div>
                  </div>
                  <div className={"dashboard-header__center"}>
                    {headerName && (
                      <div className="summaryNameText" title={headerName}>
                        {headerName}
                      </div>
                    )}
                  </div>
                  <div className={"dashboard-header__right"}>
                    {sidebarCSR && (
                      <button
                        type="button"
                        className={"dashboard-header__steps-toggle"}
                        aria-expanded={stepsOpen}
                        onClick={() => setStepsOpen(!stepsOpen)}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M2 4H14M2 8H14M2 12H14"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        Steps
                      </button>
                    )}
                    <>
                      {headerButton === "new-csr" ? (
                        <>
                          <Link
                            to={"/csr/new/summary"}
                            className={
                              "input-submit input-submit__primary input-submit__small"
                            }>
                            Create CSR
                          </Link>
                          <Link
                            to={"/ls/new/select-study"}
                            className={
                              "input-submit input-submit__primary input-submit__small"
                            }>
                            Create Lay Summary
                          </Link>
                          <Link
                            to={"/pn/new/summary"}
                            className={
                              "input-submit input-submit__primary input-submit__small"
                            }>
                            Create Patient Narrative
                          </Link>
                        </>
                      ) : (
                        <Link to={"/dashboard"} className={"close-button"}>
                          <CloseMenu />
                          Close
                        </Link>
                      )}
                    </>
                  </div>
                </div>
              )}
              <div
                className={`dashboard-content__wrapper ${(prevButton || nextButton) && "extra-footer"} ${contentOverlay ? "has-overlay" : ""} ${hideFooter ? "no-footer" : ""}`}>
                {pageContent}
                {contentOverlay && (
                  <div
                    className={"dashboard-content__overlay"}
                    data-testid="dashboard-content-overlay"
                  />
                )}
              </div>
              {(prevButton || nextButton) && (
                <div
                  className={`footer__navigation ${prevButton && nextButton ? "both-buttons" : ""} ${prevButton && !nextButton ? "only-prev-button" : ""}`}>
                  {prevButton && (
                    <button
                      onClick={() => navigate(prevButton.buttonGoToAction)}
                      className="input-submit input-submit__secondary input-submit__arrow">
                      <ArrowLeft />
                      {prevButton.buttonTitle}
                    </button>
                  )}
                  {saveButton && (
                    <CButton
                      color={"primary"}
                      variant={"outline"}
                      disabled={saveButtonDisabled || !canSave}
                      title={canSave ? undefined : NO_PERMISSION_TITLE}
                      onClick={
                        ignoreSaveErrors
                          ? async e => {
                              try {
                                const result = saveButtonHandler(e);
                                if (result?.then) await result;
                              } catch {
                                /* silently ignore save errors */
                              }
                            }
                          : e => saveButtonHandler(e)
                      }>
                      Save
                    </CButton>
                  )}
                  {nextButton &&
                    (nextButton.buttonFormAction ? (
                      <button
                        form={nextButton.buttonFormAction}
                        className="input-submit input-submit__primary input-submit__arrow"
                        disabled={nextButton.disabled}>
                        {nextButton.buttonTitle}
                        <ArrowRight />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!nextButton.disabled) {
                            handleNavigationWithSave(
                              nextButton.buttonGoToAction
                            );
                          }
                        }}
                        className="input-submit input-submit__primary input-submit__arrow"
                        disabled={nextButton.disabled}>
                        {nextButton.buttonTitle}
                        {nextButtonRightArrow ? <ArrowRight /> : ""}
                      </button>
                    ))}
                </div>
              )}
              {/* MW places the footer inside its own chat column so the
                  sessions list runs the full height of the panel, and asks for
                  the shell's copy to be left out. */}
              {!hideFooter && <Footer />}
              <Loader loading={ajaxLoading || studyInfoLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
